import { classifyAppError, type AppErrorInfo } from '@rammetec/app-kit/errors'
import { Button } from '@rammetec/design-system'
import { ErrorScreen } from '@rammetec/design-system/templates'
import { type ReactNode } from 'react'
import { useInRouterContext, useNavigate } from 'react-router-dom'
import { appInfo } from '@/config/appInfo'

export interface AppErrorScreenProps {
  error: unknown
  fit?: 'page' | 'content'
  onRetry?: () => void
  onReload?: () => void
}

function formatFooter(): string {
  const build = appInfo.buildNumber || 'local'
  return `versão ${appInfo.version} · build ${build}`
}

function GoHomeButtonWithNavigate() {
  const navigate = useNavigate()
  return (
    <Button variant="outline" onClick={() => navigate('/')}>
      Ir para o início
    </Button>
  )
}

function GoHomeButtonOutsideRouter() {
  return (
    <Button variant="outline" onClick={() => globalThis.location.assign('/')}>
      Ir para o início
    </Button>
  )
}

function GoHomeButton() {
  const inRouter = useInRouterContext()
  return inRouter ? <GoHomeButtonWithNavigate /> : <GoHomeButtonOutsideRouter />
}

function buildActions(
  info: AppErrorInfo,
  onRetry: (() => void) | undefined,
  onReload: (() => void) | undefined,
): ReactNode {
  const goHome = <GoHomeButton />

  switch (info.kind) {
    case 'stale-build':
      return onReload ? <Button onClick={onReload}>Recarregar</Button> : null
    case 'offline':
      return (
        <div className="flex flex-wrap justify-center gap-2">
          {info.canRetry && onRetry ? (
            <Button onClick={onRetry}>Tentar novamente</Button>
          ) : null}
          {goHome}
        </div>
      )
    case 'not-found':
    case 'forbidden':
      return goHome
    case 'unknown':
      return (
        <div className="flex flex-wrap justify-center gap-2">
          {info.canRetry && onRetry ? (
            <Button onClick={onRetry}>Tentar novamente</Button>
          ) : null}
          {goHome}
        </div>
      )
  }
}

export function AppErrorScreen({
  error,
  fit = 'page',
  onRetry,
  onReload,
}: AppErrorScreenProps) {
  const info = classifyAppError(error)

  return (
    <ErrorScreen
      fit={fit}
      title={info.title}
      description={info.description}
      error={info.cause}
      actions={buildActions(info, onRetry, onReload)}
      footer={formatFooter()}
    />
  )
}
